import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';

const ORG_TYPES = [
  { value: 'fpo', label: 'Farmer Producer Organization (FPO)' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'aggregator', label: 'Aggregator' },
  { value: 'company', label: 'Private Company' },
];

export default function OrgRegisterSheet({ open, onOpenChange, onRegistered }) {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    org_type: 'fpo',
    contact_email: '',
    address: '',
    gstin: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/orgs/register', form);
      toast.success(`Application submitted! Org code: ${data.organization.code}. Awaiting Frootex approval.`);
      await refreshUser();
      onOpenChange(false);
      setForm({ name: '', org_type: 'fpo', contact_email: '', address: '', gstin: '' });
      if (onRegistered) onRegistered();
      else navigate('/org');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="org-register-sheet"
        className="bg-[#F6F8FB] rounded-t-2xl border-[#E4E8EF] max-h-[90vh] max-w-md md:max-w-xl lg:max-w-2xl mx-auto p-0 flex flex-col left-1/2 -translate-x-1/2"
      >
        <SheetHeader className="text-left px-5 pt-5 pb-3 border-b border-[#E4E8EF]">
          <SheetTitle className="text-lg font-['Bricolage_Grotesque'] text-[#0B1620] flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#0B4F3F]" />
            Register your organization
          </SheetTitle>
          <SheetDescription className="text-xs text-[#5B6675]">
            Group multiple farmers under one umbrella. Frootex will review and approve your application.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <Label className="text-xs">Organization name *</Label>
            <Input
              data-testid="org-register-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Green Valley FPO"
              className="h-10 bg-[#F1F5FA] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
              <SelectTrigger data-testid="org-register-type" className="h-10 bg-[#F1F5FA] border-[#E4E8EF] rounded-lg text-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#F6F8FB] border-[#E4E8EF]">
                {ORG_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Contact email</Label>
            <Input
              data-testid="org-register-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="org@example.com"
              className="h-10 bg-[#F1F5FA] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Address</Label>
            <Input
              data-testid="org-register-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Village, District, State"
              className="h-10 bg-[#F1F5FA] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">GSTIN (optional)</Label>
            <Input
              data-testid="org-register-gstin"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              placeholder="22AAAAA0000A1Z5"
              className="h-10 bg-[#F1F5FA] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          </div>

          <div className="flex gap-2 px-5 py-3 border-t border-[#E4E8EF] bg-[#F6F8FB]">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11 border-[#E4E8EF] rounded-full text-sm"
            >
              Cancel
            </Button>
            <Button
              data-testid="org-register-submit"
              type="submit"
              disabled={submitting}
              className="flex-1 h-11 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
